import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import SearchAndOrder from '@core/interfaces/bpc-search-and-order';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

interface BulkInvoiceData {
  ids: number[];
  filters: [];
  search_and_order: SearchAndOrder;
  process_all: boolean;
  session_code: string;
}

@Component({
  name: 'bpc-download-invoice-in-pdf-modal',
  components: {},
})
export default class DownloadInvoiceInPdfModal extends Vue {
  // PROPS
  @Prop({ type: String, default: 'download-invoice-in-pdf-modall' }) readonly modalId: string;
  @Prop({ type: Boolean, default: false }) readonly loading: boolean;
  @Prop({ type: Boolean, default: false }) readonly showModal: boolean;
  @Prop(Object) entity;
  @Prop({ type: Boolean, default: false }) readonly isBulk: boolean;
  @Prop({ type: Boolean, default: false }) readonly checkedAll: boolean;
  @Prop({ type: Number, default: 0 }) readonly totalEntities: number;
  @Prop({ type: Array, default: () => [] }) readonly selectedEntities: string[];
  @Prop({
    type: Array,
    default() {
      return [];
    },
  })
  readonly selectedItemIds: number[];
  @Prop({
    type: Object,
    default() {
      return {};
    },
  })
  readonly filterModel;
  @Prop({
    type: Object,
    default() {
      return {};
    },
  })
  readonly searchAndOrder: SearchAndOrder;

  // DATA
  public requestSent: boolean = false;
  public isProcessing: boolean = false;
  public processAll: boolean = false;
  public eventsSuccess: number = 0;
  public eventsError = [];
  showModalIndex = null;
  readonly errorMessageText: string = 'global.generic.error_form_action';
  public currentSessionCode: string = null;
  public jobId: number = null;
  public maxNumberOfInvoices: number = 1200;

  // COMPUTED
  get tenantId(): number {
    return this.$store.getters['tenant/selectedTenantId'];
  }

  // LIFECYCLE
  mounted() {
    this.showModalIndex = Date.now();
    this.initVars();
    if (this.showModal) {
      this.openModal();
    }
  }

  initVars() {
    this.requestSent = false;
    this.isProcessing = false;
    this.eventsSuccess = 0;
    this.eventsError = [];
    this.currentSessionCode = null;
    this.jobId = null;
  }

  // METHODS
  closeModal() {
    this.showModalIndex = Date.now();
    this.$emit('close-modal');
    this.initVars();
  }

  openModal() {
    this.$bvModal.show(this.modalId);
  }

  onConfirm() {
    this.currentSessionCode = this.getSessionCode();
    this.listenToWebsocketDownloadInvoiceChannel(this.currentSessionCode);

    this.requestSent = true;
    this.isProcessing = true;

    const payload: BulkInvoiceData = {
      ids: this.selectedItemIds,
      filters: this.filterModel,
      search_and_order: this.searchAndOrder,
      process_all: this.processAll,
      session_code: this.currentSessionCode,
    };

    const url = this.$bpcApiRouteHelper.getEntityPath('invoices');
    this.$bpcApi.post(`${url}/generate-bulk-pdf/`, { ...payload }).subscribe(
      res => {
        console.log(res);
      },
      error => {
        this.requestSent = false;
        this.isProcessing = false;
        this.$bpcMessage.showErrorMessage({ text: this.errorMessageText });
      },
    );
  }

  getSessionCode() {
    return `download-invoices-${this.tenantId}-${this.generateUUIDv4()}`;
  }

  listenToWebsocketDownloadInvoiceChannel(sessionCode) {
    this.isProcessing = true;
    this.$bpcEcho
      .getDownloadInvoicesChannel(sessionCode)
      .listen('.BPC\\Accounting\\Events\\InvoicePdfDownloadStarted', async data => {
        this.jobId = data.jobId;
      })
      .listen('.BPC\\Accounting\\Events\\InvoicesPDFDownloaded', async data => {
        console.log('InvoicesPDFDownloaded', data);
        this.downloadPDF(data.file, data.filename);
        this.isProcessing = false;
        this.$emit('invoices-mark-as-paid-finished');
        this.$bpcEcho.leaveDownloadInvoicesChannel(sessionCode);
      })
      .listen('.BPC\\Accounting\\Events\\ChunkInvoicePdfDownloaded', data => {
        this.eventsSuccess = this.eventsSuccess + Number(data.invoicesProccesed);
      })
      .listen('.BPC\\Accounting\\Events\\ErrorDownloadingInvoicePDF', data => {
        this.eventsError.push(data);
      })
      .listen('.BPC\\Accounting\\Events\\GlobalErrorDownloadingInvoiceInPDF', data => {
        this.$bpcMessage.showErrorMessage({
          text: data.msg,
        });
        this.isProcessing = false;
        this.requestSent = false;
        this.$bpcEcho.leaveDownloadInvoicesChannel(sessionCode);
      });
  }

  async downloadPDF(filePath: string, fileName: string) {
    return new Promise((resolve, reject) => {
      const basePath = this.$bpcApiRouteHelper.getEntityPath('invoices');
      this.$bpcApi
        .download(`${basePath}/download-bulk-pdf`, `${fileName}.pdf`, {
          params: {
            filePath: filePath,
            fileName: fileName,
          },
        })
        .subscribe(
          (res: BPCGenericApiResponse) => {
            resolve(res);
          },
          error => {
            reject(error);
          },
        );
    });
  }

  async onCancel() {
    this.$bpcEcho.leaveDownloadInvoicesChannel(this.currentSessionCode);
    this.closeModal();
    // FIXME: to be finish
    // const url = this.$bpcApiRouteHelper.getEntityPath('invoices');
    // this.$bpcApi
    //   .post(`${url}/cancel-bulk-pdf/`, { jobId: this.jobId, sessionCode: this.currentSessionCode })
    //   .subscribe(
    //     res => {
    //       this.$bpcEcho.leaveDownloadInvoicesChannel(this.currentSessionCode);
    //       this.closeModal();
    //     },
    //     error => {
    //       this.requestSent = false;
    //       this.isProcessing = false;
    //       this.$bpcMessage.showErrorMessage({ text: this.errorMessageText });
    //     },
    //   );
  }

  generateUUIDv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // WATCHERS

  @Watch('showModal')
  onShowModalChange(val) {
    if (val) {
      this.openModal();
    } else {
      this.$bvModal.hide(this.modalId);
    }
  }
}
